export const COLOR_MODES = {
    GRADIENT: 'gradient',
    BRACKETS: 'brackets',
};

// Curated heatmap palette (literal hex — these are passed directly to Highcharts,
// which cannot consume CSS custom properties).
export const HEATMAP_GRADIENT_LOW_COLOR = '#DCF1F7';
export const HEATMAP_GRADIENT_MID_COLOR = '#5BB9D4';
export const HEATMAP_GRADIENT_HIGH_COLOR = '#2E5E8C';
export const HEATMAP_DEFAULT_BRACKET_COLOR = HEATMAP_GRADIENT_MID_COLOR;
export const HEATMAP_NO_MATCH_COLOR = '#E6E6E6';
export const HEATMAP_CELL_BORDER_COLOR = '#FFFFFF';
export const HEATMAP_EMPTY_CELL_COLOR = '#F2F2F2';

// Singular blue/teal, matching the approved preview mock.
export const DEFAULT_HEATMAP_GRADIENT_STOPS = [
    { stop: 0, color: HEATMAP_GRADIENT_LOW_COLOR },
    { stop: 0.5, color: HEATMAP_GRADIENT_MID_COLOR },
    { stop: 1, color: HEATMAP_GRADIENT_HIGH_COLOR },
];

// Distinct colors used to seed newly added stops/brackets so the post-add state
// doesn't collide with existing colors (which the unique-color validation rejects).
export const HEATMAP_COLOR_PALETTE = [
    HEATMAP_GRADIENT_LOW_COLOR,
    HEATMAP_GRADIENT_MID_COLOR,
    HEATMAP_GRADIENT_HIGH_COLOR,
    '#F4B03C',
    '#F37773',
    '#A75885',
    '#17A38F',
    '#B186D8',
    '#FED674',
    '#A2745E',
];

// Normalize a hex color for equality checks: lowercase and expand #rgb -> #rrggbb,
// so e.g. '#FFF' and '#ffffff' compare equal. Non-strings become ''.
export const normalizeHex = color => {
    if (typeof color !== 'string') {
        return '';
    }
    const hex = color.trim().toLowerCase();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(hex);
    return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : hex;
};

const hslToHex = h => {
    const s = 65;
    const l = 55;
    const a = (s * Math.min(l, 100 - l)) / 10000;
    const channel = n => {
        const k = (n + h / 30) % 12;
        const value = l / 100 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * value)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${channel(0)}${channel(8)}${channel(4)}`;
};

// Returns a color not already present in `usedColors` (case-insensitive): first an
// unused palette entry, then an unused generated hue. Keeps add-stop/add-bracket valid.
export const nextHeatmapColor = (usedColors = []) => {
    const used = new Set((usedColors || []).map(normalizeHex));
    const unused = HEATMAP_COLOR_PALETTE.find(c => !used.has(normalizeHex(c)));
    if (unused) {
        return unused;
    }
    for (let i = 0; i < 360; i++) {
        const candidate = hslToHex((i * 47) % 360);
        if (!used.has(normalizeHex(candidate))) {
            return candidate;
        }
    }
    return HEATMAP_GRADIENT_MID_COLOR;
};

export function defaultHeatmapOptions() {
    return {
        mode: COLOR_MODES.GRADIENT,
        stops: DEFAULT_HEATMAP_GRADIENT_STOPS.map(s => ({ ...s })),
        dataClasses: [],
        defaultColor: HEATMAP_NO_MATCH_COLOR,
        showValues: true,
    };
}

// "Show values in cells" defaults to ON: only an explicit `false` hides them, so
// unset (undefined/null on legacy widgets) and `true` both show. Single source of
// truth shared by the chart and the editor checkbox.
export const shouldShowValues = heatmapOptions => heatmapOptions?.showValues !== false;

export function buildColorAxis(heatmapOptions) {
    const options = heatmapOptions || defaultHeatmapOptions();
    const mode = options.mode || COLOR_MODES.GRADIENT;

    if (mode === COLOR_MODES.BRACKETS && options.dataClasses && options.dataClasses.length) {
        return {
            dataClasses: options.dataClasses.map(dc => {
                const out = { color: dc.color };
                if (dc.from !== undefined && dc.from !== null) out.from = dc.from;
                if (dc.to !== undefined && dc.to !== null) out.to = dc.to;
                if (dc.name) out.name = dc.name;
                return out;
            }),
        };
    }

    const stops = (options.stops && options.stops.length ? options.stops : DEFAULT_HEATMAP_GRADIENT_STOPS).map(s => [
        s.stop,
        s.color,
    ]);
    return { stops };
}
